import {
  Box,
  Heading,
  VStack,
  Body,
  Container,
  Divider,
} from '@hover/blueprint';
import React from 'react';
import { Accordion } from '../../ui';
import { useInterSectionObserver } from '../../../hooks';

const FAQ_ITEMS = [
  {
    title: 'Q: What does your interview process consist of?',
    content: (
      <VStack spacing={300}>
        <Body alignSelf="flex-start" size={500}>
          A: Our process generally goes like this:
        </Body>
        <VStack flexDir="column" spacing={400}>
          <Body size={500} fontWeight={300}>
            We review your application, looking for applicable experience and
            attributes we believe would make you successful in the role.
          </Body>
          <Body size={500} fontWeight={300}>
            You’ll have an introductory call where we’ll provide an overview of
            HOVER and the role. We’ll also begin to assess your qualifications
            and answer any questions you may have.
          </Body>
          <Body size={500} fontWeight={300}>
            You&apos;ll meet with your new manager or another team member. Here
            we’ll do a deeper dive into your background, which could include a
            live technical assessment or take-home challenge to be completed
            when convenient for you.
          </Body>
          <Body size={500} fontWeight={300}>
            Our virtual onsite interview is usually the final step in the
            interview process. You’ll meet with multiple members of the
            immediate team you’re interviewing with as well as a
            cross-functional teammate or two. We’ll assess technical and
            non-technical competencies as well as values alignment.
          </Body>
        </VStack>
        <Body size={500} fontWeight={300}>
          Please note that this is a generalization, and for a more specific
          understanding of the interview process for the role you’ve applied to
          please ask your Recruiter. Our goal is to provide ample opportunity
          for you to demonstrate your ability to succeed in the role. We believe
          the best interview processes are objective, transparent and
          consistent, and that is what we aim to provide you with.{' '}
        </Body>
      </VStack>
    ),
  },
  {
    title: 'Q: What is your current work from home policy?',
    content: (
      <VStack spacing={400}>
        <Body size={500}>
          A: We see value in having offices where employees gather and
          collaborate. We also know that employees want more flexibility in
          their work schedules so we practice a hybrid model.
        </Body>
        <Body size={500} fontWeight={300}>
          To us, hybrid means that all employees will either be ‘in office’ or
          ‘fully remote.’ Employees who are in office will be expected to go
          into the office at least 2-3 days / week. Those who are fully remote
          will have opportunities to travel to our offices at certain times.
          Some employees will need to be on-site more based on the nature of
          their roles. We will not define success by total time spent in the
          office.
        </Body>
      </VStack>
    ),
  },
  {
    title: 'Q: Will my interview be in-person or remote?',
    content: (
      <Body size={500}>
        A: All of our interviews are still being conducted remotely. We are
        prioritizing the wellbeing of our employees and candidates, as well as
        trying to provide a consistent interview process for all candidates. We
        primarily use Zoom video conferencing for our interview format.
      </Body>
    ),
  },
  {
    title: 'Q: What is HOVER’s compensation philosophy?',
    content: (
      <Body size={500}>
        A: Our goal is to have transparent, equitable, rigorous, and responsible
        compensation practices. We take a market based approach and use
        best-in-class compensation data sources to help define compensation
        bands for each role, level and location. While being paid fairly and
        competitively are important, we believe compensation is only one part of
        a much larger picture of why you join and stay at HOVER. The most
        meaningful thing we can provide is an exceptional work experience.
      </Body>
    ),
  },
  {
    title: 'Q: How should I prepare for my HOVER interview?',
    content: (
      <Body size={500}>
        A: Ahead of panel interviews with our team, your recruiter will schedule
        a call to go over what to expect and any role specific information.
        We’ll also share some additional resources along the way that include
        information about our company’s history, values, and general
        interviewing best practices. One of the best ways you can prepare for
        your interview and conversations with our team is to download the HOVER
        App and experience our product for yourself.
      </Body>
    ),
  },
  {
    title: 'Q: Does HOVER have an intern program?',
    content: (
      <Body size={500}>
        A: Yes, we absolutely have an intern program. Our summer 2021 class of
        interns were hired across six departments and enriched HOVER with
        diversity, intelligence and drive. We’ve converted several interns to
        full-time employees over the years, which is something we’re really
        proud of and expect to do more of in the future.
      </Body>
    ),
  },
  {
    title: 'Q: What benefits does HOVER offer employees?',
    content: (
      <Body size={500}>
        A: We offer employees competitive salaries, meaningful equity and
        comprehensive medical, dental and vision benefits. We have a flexible
        vacation policy and employees have access to wellbeing and learning
        resources like management training, conferences, and online learning
        platforms. We support families and have generous parental leave benefits
        and help employees navigate how to use them. We equip our teams with the
        best tools, give a stipend to purchase the right gear for working at
        home, and much more.
      </Body>
    ),
  },
];

interface Props {
  setCurrentSection: React.Dispatch<React.SetStateAction<string>>;
}

const CareersFAQ: React.FC<Props> = ({ setCurrentSection }) => {
  const faqRef = useInterSectionObserver(
    isIntersecting => {
      if (isIntersecting) {
        setCurrentSection('FAQ');
      }
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: 0.4,
    },
  );

  return (
    <Box
      maxW="100%"
      flexDir="column"
      alignItems="center"
      py={{ base: 700, desktop: 1000 }}
      ref={faqRef.elementRef}
      mb={{ base: 700, desktop: 0 }}
      position="relative"
      px={200}
    >
      <Box
        id="faq"
        position={{ desktop: 'absolute' }}
        display={{ base: 'none', desktop: 'block' }}
        w={0}
        h={0}
        top="200px"
      />
      <Heading
        fontWeight={{ base: 600, desktop: 600 }}
        size={{ base: 500, desktop: 700 }}
        marginBottom={{ base: 800, desktop: '88px' }}
      >
        Frequently Asked Questions
      </Heading>
      <Container maxW="790px">
        <Divider color="neutral.500" opacity={1} />
        {FAQ_ITEMS.map(item => (
          <Accordion
            maxW="790px"
            title={item.title}
            content={item.content}
            key={item.title}
          />
        ))}
      </Container>
    </Box>
  );
};

export default CareersFAQ;
